import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { read, utils } from "xlsx";
import axios from "axios";
import { baseAPI } from "../config";
import { toast } from "react-toastify";
import MainLayout from "../layouts/MainLayout";
import InterestImportTab from "./InterestImportTab";
import ImportDocsTab from "./ImportDocsTab";
import userStore from "../store/userStore";
import { validateImportFile } from '@claimup/shared/validators/expedient-import';
import { normalizeExpedientType } from '@claimup/shared/helpers/expedient-type';
import { EXPEDIENT_TYPES, EXPEDIENT_TYPE_LABELS } from '@claimup/shared/constants/expedient';
import {
  DocumentArrowUpIcon,
  TableCellsIcon,
  ArrowUpTrayIcon,
  DocumentCheckIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';

/**
 * Una empresa con AR importa su propia cartera (docs/ar-importadora-empresa-plan.md).
 * Su Excel no necesita la columna «ID del cliente»: se rellena con el suyo
 * antes de validar, y el servidor lo fuerza igual. Pura, para poder probarla.
 */
export const rowsForImporter = (rows, user) =>
  user?.role === 'company' ? rows.map(row => ({ ...row, user_id: String(user.id) })) : rows;

/**
 * La fase con la que va a entrar la fila, como la decide el servidor: la empresa
 * importa siempre a AR; el admin, lo que diga `tipo_expediente` o, vacío, la del
 * cliente. Sustituye a la vieja columna «Tipo», que enseñaba `tipo_reclamacion`
 * (eliminado el 15/10/2026) y hacía creer que todo entraba en extrajudicial.
 */
export const previewPhase = (row, user) => {
  if (user?.role === 'company') return EXPEDIENT_TYPE_LABELS[EXPEDIENT_TYPES.ACCOUNT_RECEIVABLE];
  const type = normalizeExpedientType(row?.tipo_expediente ?? row?.type ?? row?.expedient_type);
  return type ? EXPEDIENT_TYPE_LABELS[type] : 'La del cliente';
};

const ImportExpedients = () => {
  const { user } = userStore();
  const esEmpresa = user?.role === 'company';
  const [expedients, setExpedients] = useState([]);
  // Resultado de la validación previa del fichero. Mientras no sea válido, la
  // importación NO se puede lanzar: o entra el fichero entero o no entra nada.
  const [validation, setValidation] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("import");

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    processFile(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  const processFile = (file) => {
    if (!file || !file.name.match(/\.(xlsx|xls)$/)) {
      toast.error("Por favor, selecciona un archivo Excel válido (.xlsx, .xls)", {
      });
      return;
    }

    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(file);
    fileReader.onload = (e) => {
      try {
        const bufferArray = e.target.result;
        const wb = read(bufferArray, { type: "buffer" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = rowsForImporter(utils.sheet_to_json(ws), user);
        setExpedients(data);

        // Validación exhaustiva ANTES de dejar importar nada: mismas reglas que
        // aplica el backend (@claimup/shared/validators/expedient-import).
        const result = validateImportFile(data);
        setValidation(result);

        if (result.valid) {
          toast.success(`Archivo validado: ${data.length} expedientes listos`);
        } else {
          toast.error(`El archivo tiene ${result.totalErrors} error(es) en ${result.rows.length} fila(s). Corrígelos y vuelve a subirlo.`,
            { autoClose: 8000 }
          );
        }
      } catch (error) {
        toast.error("Error al procesar el archivo");
      }
    };
  };

  const resetFile = () => {
    setExpedients([]);
    setValidation(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const createExpedients = async (e) => {
    e.preventDefault();

    // Cinturón: aunque el botón esté deshabilitado, no se lanza nada sin que el
    // fichero entero haya pasado la validación.
    if (!validation?.valid) {
      toast.error("Corrige los errores del archivo antes de importar");
      return;
    }

    setIsProcessing(true);

    try {
      const servicios = [
        { tipo: "burofax", fecha: new Date(), estado: "pendiente", costo: 0, detalles: "Envío de burofax certificado al deudor", cantidad: 999, utilizados: 0 },
        { tipo: "llamada_robotizada", fecha: new Date(), estado: "pendiente", costo: 0, detalles: "Envío de llamada robotizada", cantidad: 999, utilizados: 0 },
        { tipo: "llamada_personal", fecha: new Date(), estado: "pendiente", costo: 0, detalles: "Envío de llamada personal", cantidad: 999, utilizados: 0 },
        { tipo: "sms", fecha: new Date(), estado: "pendiente", costo: 0, detalles: "Envío de sms", cantidad: 999, utilizados: 0 },
        { tipo: "email", fecha: new Date(), estado: "pendiente", costo: 0, detalles: "Envío de email", cantidad: 999, utilizados: 0 },
      ];

      for (const expedient of expedients) {
        expedient.servicios = servicios;

        // Tipo de expediente desde la columna del Excel (opcional). Si viene, se
        // envía como `type`; si no, el backend usa el del company por defecto.
        const rawType = expedient.tipo_expediente;
        if (rawType != null && String(rawType).trim() !== "") {
          expedient.type = String(rawType).trim();
        }

        try {
        const response = await axios.post(`${baseAPI}/expedients/add`, expedient, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        
        if (response.status === 200) {
          if (response.data.type === "create") {
            toast.success("Expediente Creado!", { autoClose: 1000 });
            // La fila entró, pero no como pedía (p. ej. AR en una empresa sin AR).
            const warnings = response.data.warnings || [];
            if (warnings.length) {
              const ref = expedient.client_internal_reference || "";
              toast.warning(`Expediente ${ref}: ${warnings.map((w) => w.message).join(" · ")}`, {
                autoClose: 8000,
              });
            }
          } else if (response.data.type === "update") {
            toast.info("Expediente Actualizado!", { autoClose: 1000 });
          }
        }
        } catch (error) {
          console.error(error);
          // El backend devuelve { error, details: [{column, message}] } cuando la
          // fila no pasa su validación: lo mostramos tal cual, no un genérico.
          const details = error?.response?.data?.details;
          const reason = Array.isArray(details)
            ? details.map((d) => d.message).join(" · ")
            : error?.response?.data?.error || "";
          const ref = expedient.client_internal_reference || expedient.id_key || "";
          toast.error(`Error en el expediente ${ref}${reason ? ": " + reason : ""}`, {
            autoClose: 8000,
          });
        }
      }

      // setTimeout(() => {
      //   window.location = "/expedients";
      // }, 2000);
    } catch (error) {
      console.error(error);
      toast.error("Error al procesar los expedientes");
    } finally {
      setIsProcessing(false);
    }
  };

  // Solo el admin y una empresa (la suya, con AR: lo decide el servidor).
  if (user?.role !== "admin" && !esEmpresa) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <MainLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          Importación de Expedientes
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {esEmpresa
            ? 'Sube vuestra cartera AR desde un archivo Excel. Los expedientes quedan a nombre de vuestra empresa; no hace falta la columna del cliente.'
            : 'Importa expedientes nuevos o actualiza los intereses desde un archivo Excel'}
        </p>
      </div>

      {/* Pestañas */}
      <div className="mb-6 border-b border-slate-200">
        <nav className="flex gap-6">
          <button
            type="button"
            onClick={() => setActiveTab("import")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "import"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            Importación
          </button>
          {/* La actualización de intereses es del admin. */}
          {!esEmpresa && (
            <button
              type="button"
              onClick={() => setActiveTab("interest")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "interest"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              Actualización de interés
            </button>
          )}
          <button
            type="button"
            onClick={() => setActiveTab("docs")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "docs"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            Documentación
          </button>
        </nav>
      </div>

      {activeTab === "interest" && !esEmpresa && (
        <div className="bg-white shadow-sm rounded-lg border border-slate-200 p-6">
          <InterestImportTab />
        </div>
      )}

      {activeTab === "docs" && (
        <div className="bg-white shadow-sm rounded-lg border border-slate-200 p-6">
          <ImportDocsTab />
        </div>
      )}

      <div className={`bg-white shadow-sm rounded-lg border border-slate-200 overflow-hidden ${activeTab === "import" ? "" : "hidden"}`}>
            <div className="p-6">
              {expedients.length === 0 ? (
                <div className="max-w-2xl mx-auto text-center">
                  {/* Badges de información */}
                  <div className="flex justify-center space-x-6 mb-8">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-lg mb-3">
                        <DocumentArrowUpIcon className="w-6 h-6 text-blue-600" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">Formato Excel</p>
                    </div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-lg mb-3">
                        <TableCellsIcon className="w-6 h-6 text-blue-600" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">Datos Estructurados</p>
                    </div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-lg mb-3">
                        <DocumentCheckIcon className="w-6 h-6 text-blue-600" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">Validación Automática</p>
                    </div>
                  </div>

                  {/* Plantilla descargable */}
                  <div className="mb-8">
                    <a
                      href="https://docs.google.com/spreadsheets/d/1JLB5RD2X6vbbukuUY4h9f3ohf8AplU--/edit?usp=share_link&ouid=110409759592739368353&rtpof=true&sd=true"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors duration-150"
                    >
                      <ArrowDownTrayIcon className="w-5 h-5 mr-2 text-slate-400" />
                      Descargar plantilla
                    </a>
                  </div>

                  {/* Área de drop */}
                  <div
                    className={`relative rounded-lg border-2 border-dashed transition-all duration-150 ${
                      isDragging
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300 bg-slate-50"
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                  >
                    <input
                      type="file"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      onChange={handleFileInput}
                      accept=".xlsx,.xls"
                    />
                    <div className="p-8">
                      <div className="mb-4">
                        <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-slate-400" />
                      </div>
                      <p className="text-sm text-slate-600 mb-1">
                        Arrastra tu archivo Excel aquí o
                        <span className="text-blue-600 font-medium"> haz clic para seleccionar</span>
                      </p>
                      <p className="text-xs text-slate-500">
                        Formatos soportados: .xlsx, .xls
                      </p>
                    </div>
                  </div>

                  {/* Puntero a la documentación completa (pestaña Documentación).
                      Se mantiene aquí solo el resumen: el detalle vive en un
                      único sitio, ImportDocsTab, para que no se desincronice. */}
                  <div className="mt-8 p-4 rounded-lg border border-slate-200 bg-slate-50 text-left">
                    <p className="text-sm font-medium text-slate-700 mb-1">
                      Antes de subir el archivo
                    </p>
                    <p className="text-sm text-slate-600 mb-3">
                      Se valida el fichero entero: si alguna fila tiene un error no se importa
                      nada, y verás el listado de errores por fila y columna. Consulta las
                      columnas obligatorias, el formato de los teléfonos y el de las fechas en la
                      pestaña de documentación.
                    </p>
                    <button
                      type="button"
                      onClick={() => setActiveTab("docs")}
                      className="inline-flex items-center px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-blue-600 hover:bg-slate-50 transition-colors duration-150"
                    >
                      <BookOpenIcon className="w-5 h-5 mr-2 text-slate-400" />
                      Ver documentación de la importación
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Resumen de importación */}
                  <div
                    className={`mb-6 p-4 rounded-lg border ${
                      validation?.valid
                        ? "bg-blue-50 border-blue-100"
                        : "bg-red-50 border-red-100"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {validation?.valid ? (
                          <>
                            <DocumentCheckIcon className="w-5 h-5 text-blue-600 mr-2" />
                            <span className="text-sm font-medium text-slate-700">
                              {expedients.length} expedientes validados y listos para importar
                            </span>
                          </>
                        ) : (
                          <>
                            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
                            <span className="text-sm font-medium text-slate-700">
                              {validation?.totalErrors} error(es) en {validation?.rows.length} de{" "}
                              {expedients.length} filas — corrige el Excel y vuelve a subirlo
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-2 border border-slate-200 bg-white text-sm font-medium rounded-lg text-slate-600 hover:bg-slate-50 transition-colors duration-150"
                          onClick={resetFile}
                          disabled={isProcessing}
                        >
                          Subir otro archivo
                        </button>
                      <button
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-150 ${
                          isProcessing || !validation?.valid
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        onClick={createExpedients}
                        disabled={isProcessing || !validation?.valid}
                      >
                        {isProcessing ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Procesando...
                          </>
                        ) : (
                          <>
                            <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
                            Importar expedientes
                          </>
                        )}
                      </button>
                      </div>
                    </div>
                  </div>

                  {/* Informe de errores de validación (bloquea la importación) */}
                  {validation && !validation.valid && (
                    <div className="mb-6 rounded-lg border border-red-200 overflow-hidden">
                      <div className="px-4 py-3 bg-red-50 border-b border-red-200">
                        <p className="text-sm font-medium text-red-800">
                          Errores encontrados
                        </p>
                        <p className="text-xs text-red-700 mt-1">
                          La fila indicada es la del Excel (la 1 son las cabeceras). No se
                          importará nada hasta que no quede ningún error.
                        </p>
                      </div>
                      <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                        {validation.rows.map((row) => (
                          <div key={row.index} className="px-4 py-3 bg-white">
                            <p className="text-sm font-medium text-slate-700 mb-1">
                              Fila {row.line}
                            </p>
                            <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1">
                              {row.errors.map((err, i) => (
                                <li key={`${row.index}-${i}`}>
                                  <code className="px-1 bg-slate-100 rounded-lg text-xs">
                                    {err.column}
                                  </code>{" "}
                                  {err.message}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {validation?.valid && (
                    <div className="mb-6 flex items-center px-4 py-3 rounded-lg border border-emerald-200 bg-emerald-50">
                      <CheckCircleIcon className="w-5 h-5 text-emerald-600 mr-2 shrink-0" />
                      <p className="text-sm text-emerald-800">
                        Todas las filas cumplen las validaciones (campos obligatorios, importes,
                        fechas, emails, teléfonos, divisa, tipo y referencias sin duplicar).
                      </p>
                    </div>
                  )}

                  {/* Tabla de previsualización */}
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="table-auto w-full">
                        <thead className="text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="px-3 py-4 whitespace-nowrap">
                              <div className="font-semibold text-left">ID Key</div>
                            </th>
                            <th className="px-3 py-4 whitespace-nowrap">
                              <div className="font-semibold text-left">User ID</div>
                            </th>
                            <th className="px-3 py-4 whitespace-nowrap">
                              <div className="font-semibold text-left">Deudor</div>
                            </th>
                            <th className="px-3 py-4 whitespace-nowrap">
                              <div className="font-semibold text-left">Importe</div>
                            </th>
                            <th className="px-3 py-4 whitespace-nowrap">
                              <div className="font-semibold text-left">Fase</div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-slate-200">
                          {expedients.map((expedient) => (
                            <tr key={expedient.id_key}>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="font-medium text-slate-800">{expedient.id_key}</div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="text-slate-800">{expedient.user_id}</div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="text-slate-800">{expedient.deudor}</div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="font-medium text-emerald-600">{expedient.importe_deuda}€</div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="text-slate-800">{previewPhase(expedient, user)}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
    </MainLayout>
  );
};

export default ImportExpedients;
